import { useWeb3React } from "@web3-react/core";
import { InjectedConnector } from "@web3-react/injected-connector";
import { WalletLinkConnector } from "@web3-react/walletlink-connector";
import { ethers } from "ethers";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { User } from "../common/models/User";
import SimpleButton from "../components/Buttons/SimpleButton";
import { NavBar } from "../components/NavBar";
import Text from "../components/Text/Text";
import { useAuth } from "../hooks/useAuth";
import coinbase_logo from "../imgs/coinbase_logo.svg";
import metamask_fox from "../imgs/metamask_fox.svg";

interface ConnectWalletProps {
  theme: string;
  toggleTheme: any;
}

const ConnectWalletPageContainer = styled.div`
  background: #000d30;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100vw;
  height: 100vh;
  padding-top: 4%;
`;

const MetaMaskConnectButton = styled("div")`
  position: relative;
  display: flex;
  background: #000d30;
  border: 1px solid #ffffff90;
  border-radius: 15px;
  padding: 10px;
`;

const CoinbaseConnectButton = styled(MetaMaskConnectButton)`
  margin-top: 5%;
`;
const ConnectWalletContainer = styled.div`
  padding: 1% 3% 3% 3%;
  font-size: 12px;
  background-color: ${(props) =>
    props.theme.themeType === "dark" ? "#000D30" : "#879AE4"};
  border-radius: 30px;
  border: 0.5px solid #ffffff90;
  background: #000d30;
  backdrop-filter: blur(19.6px);
  min-width: 20%;

  @media screen and (max-width: 650px) {
    display: none;
  }
`;

const LogIntoDesktopText = styled(Text)`
  display: none;
  @media screen and (max-width: 650px) {
    display: block;
  }
`;

const ConnectWalletImg = styled.img`
  width: 10%;
  height: 10%;
  align-self: center;
`;

const TitleText = styled(Text)`
  font-size: 32px;
  margin-top: 5%;

  @media screen and (max-width: 650px) {
    margin-top: 20%;
    padding-inline: 5%;
    text-align: center;
    
  }
`;

type LocationProps = {
  state: {
    from: Location;
  };
};

export const ConnectWalletPage = ({
  theme,
  toggleTheme,
}: ConnectWalletProps) => {
  const navigate = useNavigate();
  const location = useLocation() as unknown as LocationProps;
  const from = location.state === null ? "/" : location.state.from.pathname;
  const auth = useAuth();

  const networkMap = {
    POLYGON_MAINNET: {
      chainId: ethers.utils.hexValue(137), // '0x89'
      chainName: "Matic(Polygon) Mainnet",
      nativeCurrency: { name: "MATIC", symbol: "MATIC", decimals: 18 },
      rpcUrls: ["https://polygon-rpc.com"],
      blockExplorerUrls: ["https://www.polygonscan.com/"],
    },
    MUMBAI_TESTNET: {
      chainId: ethers.utils.hexValue(80001), // '0x13881'
      chainName: "Matic(Polygon) Mumbai Testnet",
      nativeCurrency: { name: "tMATIC", symbol: "tMATIC", decimals: 18 },
      rpcUrls: ["https://rpc-mumbai.maticvigil.com"],
      blockExplorerUrls: ["https://mumbai.polygonscan.com/"],
    },
  };
  const usePolygonMainNet =
    process.env.REACT_APP_USE_POLYGON_MAINNET === "true";

  const CoinbaseWallet = new WalletLinkConnector({
    url: usePolygonMainNet
      ? process.env.REACT_APP_ALCHEMY_URL_POLYGON_MAINNET
      : process.env.REACT_APP_ALCHEMY_URL_MUMBAI,
    appName: "ComposeNFT",
    supportedChainIds: [137, 80001],
  });
  const Injected = new InjectedConnector({
    supportedChainIds: [137, 80001],
  });

  const { active, account, activate, deactivate } = useWeb3React();

  const activateInjectedProvider = async (
    providerName: "MetaMask" | "CoinBase"
  ) => {
    const { ethereum } = window;
    if (!ethereum?.providers) {
      return undefined;
    }

    let provider;
    switch (providerName) {
      case "CoinBase":
        provider = ethereum.providers.find(
          ({ isCoinbaseWallet }) => isCoinbaseWallet
        );
        break;
      case "MetaMask":
        provider = ethereum.providers.find(({ isMetaMask }) => isMetaMask);
        break;
    }

    if (provider) {

      ethereum.setSelectedProvider(provider);
      localStorage.setItem("connectedWalletProvider", providerName);
      provider.on("accountsChanged", (accounts) => {
        if (accounts.length === 0) {
          onSignOutClick();
        }
      });
    }
  };

  useEffect(() => {
    if (active) {
      auth.signin(
        account,
        (user: User) => {
          navigate(from, { replace: true });
        },
        (errorMessage) => {
        }
      );
    }
  }, [active]);

  useEffect(() => {
    let provider = null;
    if (!window.ethereum?.providers) {
      // Only one provider avaiable, so switch networks using one
      if (window.ethereum) {
        provider = window.ethereum;
        provider.on("accountsChanged", (accounts) => {
          if (accounts.length === 0) {
            onSignOutClick();
          }
        });
      }
    } else {
      if (window.ethereum.selectedProvider) {
        provider = window.ethereum.selectedProvider;
        provider.on("accountsChanged", (accounts) => {
          if (accounts.length === 0) {
            onSignOutClick();
          }
        });
      }
    }
  });

  const onSignOutClick = async () => {
    navigate("/");
    deactivate();
    localStorage.removeItem("connectedWalletProvider");
    if (auth) {
      auth.signout(() => {
        auth.user = null;
      });
    }
  };

  const switchNetwork = async () => {
    const network = usePolygonMainNet
      ? networkMap.POLYGON_MAINNET
      : networkMap.MUMBAI_TESTNET;
    let provider = null;
    if (!window.ethereum?.providers) {
      // Only one provider avaiable, so switch networks using one
      provider = window.ethereum;
    } else {
      provider = window.ethereum.selectedProvider;
    }
    try {
      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: network.chainId }],
      });
    } catch (switchError) {
      if (switchError.code === 4902) {
        try {
          await provider.request({
            method: "wallet_addEthereumChain",
            params: [network],
          });
        } catch (error) {
        }
      }
    }
  };

  const onConnectWalletClick = async () => {
    activateInjectedProvider("MetaMask");
    await switchNetwork();
    activate(Injected);
  };
  const onConnectCoinbaseClick = async () => {
    activateInjectedProvider("CoinBase");
    await switchNetwork();
    activate(CoinbaseWallet);
  };

  return (
    <>
      <NavBar theme={theme} themeToggler={toggleTheme} />
      <ConnectWalletPageContainer>
        <TitleText bold={true}>Connect Your Wallet To Continue</TitleText>
        <LogIntoDesktopText>Please use your desktop to continue</LogIntoDesktopText>

        <ConnectWalletContainer>
          <Text size={"16px"}>My Wallet</Text>
          <MetaMaskConnectButton>
            <ConnectWalletImg src={metamask_fox} />
            <Text style={{ marginLeft: "5%" }} size={"14px"}>
              Metamask
            </Text>
            <SimpleButton
              style={{ marginLeft: "auto" }}
              onClick={onConnectWalletClick}
            >
              Connect
            </SimpleButton>
          </MetaMaskConnectButton>
          <CoinbaseConnectButton>
            <ConnectWalletImg src={coinbase_logo} />
            <Text style={{ marginLeft: "5%" }} size={"14px"}>
              Coinbase
            </Text>
            <SimpleButton
              style={{ marginLeft: "auto" }}
              onClick={onConnectCoinbaseClick}
            >
              Connect
            </SimpleButton>
          </CoinbaseConnectButton>
        </ConnectWalletContainer>
      </ConnectWalletPageContainer>
    </>
  );
};
